import React from 'react';
import {
  LoginPageProps,
  LoginFormTypes,
  useLink,
  useRouterType,
  useRouterContext,
  useNavigation,
  useTranslation,
} from '@refinedev/core';
import {
  bodyStyles,
  cardTitle,
  containerStyles,
  headStyles,
  titleStyles,
} from './styles';
import {
  Row,
  Col,
  Layout,
  Card,
  Typography,
  Form,
  Input,
  Button,
  CardProps,
  LayoutProps,
  FormProps,
  theme,
  message,
} from 'antd';
import { AppIcon } from '../../../assets/icons';
import { LanguageSwitcher } from '@/components/layout/changeLang';
import { colors } from '../../constants';
import { TOKEN_KEY } from '@/authProvider';
import { setAuthToken } from '@/rest-data-provider/utils/axios';
import { signIn, useProfileStore } from '@/core';
import useAPI from '@/api/useApi';
import { endPoints } from '@/api/configs';
import { LogInRes } from '@/api/types/authTypes';
import { motion } from 'framer-motion';
type LoginProps = LoginPageProps<LayoutProps, CardProps, FormProps>;

/**
 * **refine** has a default login page form which is served on `/login` route when the `authProvider` configuration is provided.
 *
 * @see {@link https://refine.dev/docs/ui-frameworks/antd/components/antd-auth-page/#login} for more details.
 */
export const LoginPage: React.FC<LoginProps> = ({
  forgotPasswordLink,
  contentProps,
  wrapperProps,
  formProps,
  hideForm,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm<LoginFormTypes>();
  const routerType = useRouterType();
  const Link = useLink();
  const { Link: LegacyLink } = useRouterContext();
  const { push } = useNavigation();
  const { translate } = useTranslation();
  const ActiveLink = routerType === 'legacy' ? LegacyLink : Link;
  const { setUserData, setAuthToken: setAuthTokenStore } = useProfileStore();
  const { usePost } = useAPI();
  const { mutate: signInAction, isLoading } = usePost<
    LogInRes,
    {
      email?: string;
      password?: string;
    }
  >(endPoints.login);
  const onSubmit = (values: LoginFormTypes) => {
    signInAction(
      {
        email: values.email,
        password: values.password,
      },
      {
        onSuccess: (data) => {
          const res = data.data.data;
          setUserData(res);
          setAuthToken(res.access_token);
          setAuthTokenStore(res.access_token);
          signIn({
            accessToken: res.access_token,
            refreshToken: res.refresh_token,
          });
          localStorage.setItem(TOKEN_KEY, res.account.email);
          push('/');
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };

  const CardTitle = (
    <div style={cardTitle}>
      <AppIcon />
      <Typography.Title
        level={3}
        style={{
          ...titleStyles,
        }}
      >
        {translate('pages.login.title')}
      </Typography.Title>
    </div>
  );

  const CardContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card
        title={CardTitle}
        style={{
          ...containerStyles,
          backgroundColor: token.colorBgElevated,
        }}
        styles={{ header: headStyles, body: bodyStyles }}
        {...(contentProps ?? {})}
      >
        {!hideForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Form<LoginFormTypes>
              layout="vertical"
              form={form}
              onFinish={onSubmit}
              requiredMark={false}
              initialValues={{
                remember: false,
              }}
              {...formProps}
            >
              <motion.div
                initial={{ x: -20 }}
                animate={{ x: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <Form.Item
                  name="email"
                  label={translate('pages.login.fields.email')}
                  rules={[
                    {
                      required: true,
                      message: translate('pages.errors.required'),
                    },
                    {
                      type: 'email',
                      message: translate('pages.login.errors.validEmail'),
                      whitespace: true,
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder={translate('pages.login.fields.email')}
                  />
                </Form.Item>
              </motion.div>

              <motion.div
                initial={{ x: -20 }}
                animate={{ x: 0 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <Form.Item
                  name="password"
                  label={translate('pages.login.fields.password')}
                >
                  <Input.Password
                    autoComplete="current-password"
                    placeholder="●●●●●●●●"
                    size="large"
                  />
                </Form.Item>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '24px',
                }}
              >
                {forgotPasswordLink ?? (
                  <ActiveLink
                    style={{
                      color: colors.primary,
                      fontSize: '12px',
                      marginLeft: 'auto',
                    }}
                    to="/forgot-password"
                  >
                    {translate('pages.login.buttons.forgotPassword')}
                  </ActiveLink>
                )}
              </motion.div>

              {!hideForm && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <Form.Item>
                    <Button
                      type="primary"
                      size="large"
                      htmlType="submit"
                      block
                      style={{
                        backgroundColor: token.colorPrimary,
                      }}
                      loading={isLoading}
                    >
                      {translate('pages.login.buttons.submit')}
                    </Button>
                  </Form.Item>
                </motion.div>
              )}
            </Form>
          </motion.div>
        )}
      </Card>
    </motion.div>
  );

  return (
    <Layout {...(wrapperProps ?? {})}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          position: 'absolute',
          top: '24px',
          right: '24px',
          zIndex: 999,
        }}
      >
        <LanguageSwitcher />
      </motion.div>

      <Row
        justify="center"
        align={hideForm ? 'top' : 'middle'}
        style={{
          padding: '16px 0',
          minHeight: '100dvh',
          paddingTop: hideForm ? '15dvh' : '16px',
        }}
      >
        <Col xs={22}>{CardContent}</Col>
      </Row>
    </Layout>
  );
};
